import { Dispatch, MutableRefObject, SetStateAction, useCallback, useMemo } from 'react';
import { useGetRepositoryByIdQuery, useRepositoryEntryUpdateMutation } from '../services/repositoryEntries';
import {
	applyReferenceSnap,
	buildAxisBounds,
	CanvasEntryLayoutRow,
	computeEntriesLinkedToGridAtAnchor,
	expandIdsBySwimlaneRoots,
	findSwimlaneLaneRootIdForCollectionPosition,
	isGridSnapDisabled,
	resolveReferenceSnapMode,
} from '../utils/canvasGridUtils';
import { mergePropertyDefinitions, resolveGridAxisSnapFields } from '../utils/resolveGridAxisSnap';
import { entriesUpdateMany, repositoryEntryUpdateLocalAction } from '../containers/contentSlice';
import { CanvasPosition, Entry, EntityType, GridEntry, NoteEntry, PropertyDefinition, ReferenceEntry } from '../types';
import { useAppDispatch } from '../hooks';
import { useEntryRepositionMutation } from '../services/records';
import { AnchorLineSpec, getSnappedAnchorId as getSnappedAnchorIdUtil, ANCHOR_SNAP_EPSILON } from '../utils/canvasAnchorUtils';

const GRID_X_SNAP_THRESHOLD = 100;
const GRID_AXIS_PADDING = 200;
const GRID_AXIS_PADDING_VIRTUAL_INFINITY = 100000;
const GRID_SNAP_EPSILON = 1;

type DragSnapMode = 'vertical' | 'horizontal' | null;
type SwimlaneCoord = { orientation: 'vertical' | 'horizontal'; coord: number } | null;
type Lane = { rootId: string; coord: number; min: number; max: number };
type GridAxisBounds = { minX: number; maxX: number; minY: number; maxY: number } | null;
type GroupDragState = {
	draggedId: string;
	selectedIds: string[];
	startById: Map<string, CanvasPosition>;
	draggedStart: CanvasPosition;
} | null;

type UseCanvasGridArgs = {
	visibleEntries: Entry[];
	entriesWithCanvasPositions: CanvasEntryLayoutRow[];
	allPropertyDefinitions: PropertyDefinition[];
	isAuthenticated: boolean;
	draggingReferenceRepositoryId: string | null;
	dragSnapMode: DragSnapMode;
	activeSwimlaneCoord: SwimlaneCoord;
	setActiveSwimlaneCoord: Dispatch<SetStateAction<SwimlaneCoord>>;
	dragSnapModeRef: MutableRefObject<DragSnapMode>;
	setDragSnapMode: Dispatch<SetStateAction<DragSnapMode>>;
	selectedEntryIdsRef: MutableRefObject<string[]>;
	groupDragStateRef: MutableRefObject<GroupDragState>;
	getPosition: (entry: Entry, index: number) => CanvasPosition;
	verticalAxisNodeRef: MutableRefObject<HTMLDivElement | null>;
	horizontalAxisNodeRef: MutableRefObject<HTMLDivElement | null>;
	projectedVerticalLaneNodeByCoordRef: MutableRefObject<Map<string, HTMLDivElement>>;
	projectedHorizontalLaneNodeByCoordRef: MutableRefObject<Map<string, HTMLDivElement>>;
	anchorAxisLines?: AnchorLineSpec[];
};

/**
 * @deprecated Grid creation now produces a system of anchors. This hook remains to support
 * existing GridEntry elements but should not be extended.
 */
export default function useCanvasGrid({
	visibleEntries,
	entriesWithCanvasPositions,
	allPropertyDefinitions,
	isAuthenticated,
	draggingReferenceRepositoryId,
	dragSnapMode,
	activeSwimlaneCoord,
	setActiveSwimlaneCoord,
	dragSnapModeRef,
	setDragSnapMode,
	selectedEntryIdsRef,
	groupDragStateRef,
	getPosition,
	verticalAxisNodeRef,
	horizontalAxisNodeRef,
	projectedVerticalLaneNodeByCoordRef,
	projectedHorizontalLaneNodeByCoordRef,
	anchorAxisLines,
}: UseCanvasGridArgs) {
	const dispatch = useAppDispatch();
	const [entryRepositionMutation] = useEntryRepositionMutation();
	const [repositoryEntryUpdateMutation] = useRepositoryEntryUpdateMutation();
	const activeGridWithPosition = useMemo(() => {
		const gridIndex = visibleEntries.findIndex((entry) => entry.type === EntityType.Grid);
		if (gridIndex < 0) return null;
		const gridEntry = visibleEntries[gridIndex] as GridEntry;
		const position = gridEntry.xy ?? getPosition(gridEntry, gridIndex);
		return { entry: gridEntry, position };
	}, [getPosition, visibleEntries]);

	const activeGridEntry = activeGridWithPosition?.entry ?? null;
	const gridSnapX = activeGridWithPosition?.position.x;
	const gridSnapY = activeGridWithPosition?.position.y;
	const gridCollectionRepositoryId = activeGridEntry?.repositoryId ?? '';

	const { data: repositoryByIdForGridSnap } = useGetRepositoryByIdQuery(gridCollectionRepositoryId, {
		skip: !isAuthenticated || !gridCollectionRepositoryId,
	});

	const propertyDefinitionsForGridSnap = useMemo(
		() => mergePropertyDefinitions(allPropertyDefinitions, repositoryByIdForGridSnap?.propertyDefinitions),
		[isAuthenticated, repositoryByIdForGridSnap?.propertyDefinitions, allPropertyDefinitions],
	);

	const activeGridAxisSnap = useMemo(
		() => (activeGridEntry ? resolveGridAxisSnapFields(activeGridEntry, propertyDefinitionsForGridSnap) : null),
		[activeGridEntry, propertyDefinitionsForGridSnap],
	);

	const isDraggingYAxisReference = Boolean(
		activeGridAxisSnap?.yAxisRepositoryId
		&& draggingReferenceRepositoryId === activeGridAxisSnap.yAxisRepositoryId
	);
	const isDraggingXAxisReference = Boolean(
		activeGridAxisSnap?.xAxisRepositoryId
		&& draggingReferenceRepositoryId === activeGridAxisSnap.xAxisRepositoryId
	);
	const isDraggingCollectionReference = Boolean(
		activeGridAxisSnap?.repositoryId
		&& draggingReferenceRepositoryId === activeGridAxisSnap.repositoryId
	);

	const gridAxisBounds: GridAxisBounds | null = useMemo(() => {
		if (!visibleEntries.length || !activeGridEntry || !activeGridAxisSnap) return null;
		const axisSnap = activeGridAxisSnap;
		const positions = visibleEntries
			.map((entry, index) => ({ entry, position: getPosition(entry, index) }))
			.filter(({ entry, position }) => {
				if (entry.type !== EntityType.Reference) return false;
				const repositoryId = (entry as ReferenceEntry).repositoryId;
				const snappedToVerticalAxis = Boolean(axisSnap.yAxisRepositoryId)
					&& repositoryId === axisSnap.yAxisRepositoryId
					&& typeof gridSnapX === 'number'
					&& Math.abs(position.x - gridSnapX) <= GRID_SNAP_EPSILON;
				const snappedToHorizontalAxis = Boolean(axisSnap.xAxisRepositoryId)
					&& repositoryId === axisSnap.xAxisRepositoryId
					&& typeof gridSnapY === 'number'
					&& Math.abs(position.y - gridSnapY) <= GRID_SNAP_EPSILON;
				return snappedToVerticalAxis || snappedToHorizontalAxis;
			})
			.map(({ position }) => position);
		if (typeof gridSnapX === 'number' && typeof gridSnapY === 'number') positions.push({ x: gridSnapX, y: gridSnapY });
		const xPadding = isDraggingXAxisReference
			? (dragSnapMode === 'horizontal' ? GRID_AXIS_PADDING_VIRTUAL_INFINITY : GRID_AXIS_PADDING)
			: 0;
		const yPadding = isDraggingYAxisReference
			? (dragSnapMode === 'vertical' ? GRID_AXIS_PADDING_VIRTUAL_INFINITY : GRID_AXIS_PADDING)
			: 0;
		const xBounds = buildAxisBounds({ anchor: gridSnapX, points: positions.map((pos) => pos.x), padding: xPadding });
		const yBounds = buildAxisBounds({ anchor: gridSnapY, points: positions.map((pos) => pos.y), padding: yPadding });
		if (!xBounds || !yBounds) return null;
		return { minX: xBounds.min, maxX: xBounds.max, minY: yBounds.min, maxY: yBounds.max };
	}, [activeGridAxisSnap, activeGridEntry, dragSnapMode, getPosition, gridSnapX, gridSnapY, isDraggingXAxisReference, isDraggingYAxisReference, visibleEntries]);

	const snappedReferencesByAxis = useMemo(() => {
		if (!activeGridAxisSnap || typeof gridSnapX !== 'number' || typeof gridSnapY !== 'number') {
			return { xAxis: [] as Array<{ id: string; x: number; y: number }>, yAxis: [] as Array<{ id: string; x: number; y: number }> };
		}
		const positionedReferences = visibleEntries
			.map((entry, index) => ({ entry, position: getPosition(entry, index) }))
			.filter(({ entry }) => entry.type === EntityType.Reference);
		const xAxis = positionedReferences
			.filter(({ entry, position }) => Boolean(activeGridAxisSnap.xAxisRepositoryId)
				&& (entry as ReferenceEntry).repositoryId === activeGridAxisSnap.xAxisRepositoryId
				&& Math.abs(position.y - gridSnapY) <= GRID_SNAP_EPSILON)
			.map(({ entry, position }) => ({ id: entry.id, x: position.x, y: position.y }));
		const yAxis = positionedReferences
			.filter(({ entry, position }) => Boolean(activeGridAxisSnap.yAxisRepositoryId)
				&& (entry as ReferenceEntry).repositoryId === activeGridAxisSnap.yAxisRepositoryId
				&& Math.abs(position.x - gridSnapX) <= GRID_SNAP_EPSILON)
			.map(({ entry, position }) => ({ id: entry.id, x: position.x, y: position.y }));
		return { xAxis, yAxis };
	}, [activeGridAxisSnap, getPosition, GRID_SNAP_EPSILON, gridSnapX, gridSnapY, visibleEntries]);

	const collectionReferencesWithPosition = useMemo(() => {
		if (!activeGridAxisSnap?.repositoryId) return [] as Array<{ id: string; x: number; y: number }>;
		return visibleEntries
			.map((entry, index) => ({ entry, position: getPosition(entry, index) }))
			.filter(({ entry }) => entry.type === EntityType.Reference && (entry as ReferenceEntry).repositoryId === activeGridAxisSnap.repositoryId)
			.map(({ entry, position }) => ({ id: entry.id, x: position.x, y: position.y }));
	}, [activeGridAxisSnap?.repositoryId, getPosition, visibleEntries]);

	const projectedSwimlaneVertical = useMemo(() => {
		if (!activeGridAxisSnap?.xAxisSwimlaneEnabled) return [] as Lane[];
		return snappedReferencesByAxis.xAxis.map((root) => {
			const lanePadding = isDraggingCollectionReference
				? (dragSnapMode === 'vertical' && activeSwimlaneCoord?.orientation === 'vertical' && Math.abs(root.x - activeSwimlaneCoord.coord) <= GRID_SNAP_EPSILON
					? GRID_AXIS_PADDING_VIRTUAL_INFINITY
					: GRID_AXIS_PADDING)
				: 0;
			const laneReferences = collectionReferencesWithPosition.filter((reference) => Math.abs(reference.x - root.x) <= GRID_SNAP_EPSILON);
			const yBounds = buildAxisBounds({ anchor: root.y, points: laneReferences.map((reference) => reference.y), padding: lanePadding });
			return { rootId: root.id, coord: root.x, min: yBounds?.min ?? root.y, max: yBounds?.max ?? root.y };
		});
	}, [activeGridAxisSnap?.xAxisSwimlaneEnabled, activeSwimlaneCoord, collectionReferencesWithPosition, dragSnapMode, isDraggingCollectionReference, snappedReferencesByAxis.xAxis]);

	const projectedSwimlaneHorizontal = useMemo(() => {
		if (!activeGridAxisSnap?.yAxisSwimlaneEnabled) return [] as Lane[];
		return snappedReferencesByAxis.yAxis.map((root) => {
			const lanePadding = isDraggingCollectionReference
				? (dragSnapMode === 'horizontal' && activeSwimlaneCoord?.orientation === 'horizontal' && Math.abs(root.y - activeSwimlaneCoord.coord) <= GRID_SNAP_EPSILON
					? GRID_AXIS_PADDING_VIRTUAL_INFINITY
					: GRID_AXIS_PADDING)
				: 0;
			const laneReferences = collectionReferencesWithPosition.filter((reference) => Math.abs(reference.y - root.y) <= GRID_SNAP_EPSILON);
			const xBounds = buildAxisBounds({ anchor: root.x, points: laneReferences.map((reference) => reference.x), padding: lanePadding });
			return { rootId: root.id, coord: root.y, min: xBounds?.min ?? root.x, max: xBounds?.max ?? root.x };
		});
	}, [activeGridAxisSnap?.yAxisSwimlaneEnabled, activeSwimlaneCoord, collectionReferencesWithPosition, dragSnapMode, isDraggingCollectionReference, snappedReferencesByAxis.yAxis]);

	const swimlaneChildIdsByRootId = useMemo(() => {
		const byRoot = new Map<string, Set<string>>();
		for (const lane of projectedSwimlaneVertical) {
			const childIds = collectionReferencesWithPosition
				.filter((reference) => reference.y >= lane.min && reference.y <= lane.max && Math.abs(reference.x - lane.coord) <= GRID_SNAP_EPSILON)
				.map((reference) => reference.id);
			if (!byRoot.has(lane.rootId)) byRoot.set(lane.rootId, new Set<string>());
			childIds.forEach((id) => byRoot.get(lane.rootId)?.add(id));
		}
		for (const lane of projectedSwimlaneHorizontal) {
			const childIds = collectionReferencesWithPosition
				.filter((reference) => reference.x >= lane.min && reference.x <= lane.max && Math.abs(reference.y - lane.coord) <= GRID_SNAP_EPSILON)
				.map((reference) => reference.id);
			if (!byRoot.has(lane.rootId)) byRoot.set(lane.rootId, new Set<string>());
			childIds.forEach((id) => byRoot.get(lane.rootId)?.add(id));
		}
		return new Map(Array.from(byRoot.entries()).map(([id, set]) => [id, Array.from(set)]));
	}, [collectionReferencesWithPosition, projectedSwimlaneHorizontal, projectedSwimlaneVertical]);

	const getIdsLinkedToGridAtAnchor = useCallback((grid: GridEntry, anchorX: number, anchorY: number) => {
		const snap = resolveGridAxisSnapFields(grid, propertyDefinitionsForGridSnap);
		return computeEntriesLinkedToGridAtAnchor(snap, { x: anchorX, y: anchorY }, entriesWithCanvasPositions, GRID_SNAP_EPSILON);
	}, [entriesWithCanvasPositions, propertyDefinitionsForGridSnap]);

	const expandSelectedIdsWithSwimlaneChildren = useCallback(
		(selectedIds: string[]) => expandIdsBySwimlaneRoots(selectedIds, swimlaneChildIdsByRootId),
		[swimlaneChildIdsByRootId]
	);

	const applyGridAxisPreviewDelta = useCallback((deltaX: number, deltaY: number) => {
		if (verticalAxisNodeRef.current && typeof gridSnapX === 'number' && gridAxisBounds) {
			verticalAxisNodeRef.current.style.transform = `translate(${gridSnapX + deltaX}px, ${gridAxisBounds.minY + deltaY}px)`;
		}
		if (horizontalAxisNodeRef.current && typeof gridSnapY === 'number' && gridAxisBounds) {
			horizontalAxisNodeRef.current.style.transform = `translate(${gridAxisBounds.minX + deltaX}px, ${gridSnapY + deltaY}px)`;
		}
		projectedSwimlaneVertical.forEach((lane) => {
			const node = projectedVerticalLaneNodeByCoordRef.current.get(String(lane.coord));
			if (!node) return;
			node.style.transform = `translate(${lane.coord + deltaX}px, ${lane.min + deltaY}px)`;
		});
		projectedSwimlaneHorizontal.forEach((lane) => {
			const node = projectedHorizontalLaneNodeByCoordRef.current.get(String(lane.coord));
			if (!node) return;
			node.style.transform = `translate(${lane.min + deltaX}px, ${lane.coord + deltaY}px)`;
		});
	}, [gridAxisBounds, gridSnapX, gridSnapY, horizontalAxisNodeRef, projectedHorizontalLaneNodeByCoordRef, projectedSwimlaneHorizontal, projectedSwimlaneVertical, projectedVerticalLaneNodeByCoordRef, verticalAxisNodeRef]);

	const applySwimlanePreviewDeltaForRoot = useCallback((rootId: string, deltaX: number, deltaY: number) => {
		projectedSwimlaneVertical.filter((lane) => lane.rootId === rootId).forEach((lane) => {
			const node = projectedVerticalLaneNodeByCoordRef.current.get(String(lane.coord));
			if (!node) return;
			node.style.transform = `translate(${lane.coord + deltaX}px, ${lane.min + deltaY}px)`;
		});
		projectedSwimlaneHorizontal.filter((lane) => lane.rootId === rootId).forEach((lane) => {
			const node = projectedHorizontalLaneNodeByCoordRef.current.get(String(lane.coord));
			if (!node) return;
			node.style.transform = `translate(${lane.min + deltaX}px, ${lane.coord + deltaY}px)`;
		});
	}, [projectedHorizontalLaneNodeByCoordRef, projectedSwimlaneHorizontal, projectedSwimlaneVertical, projectedVerticalLaneNodeByCoordRef]);

	const syncSwimlaneBelongsToFromPosition = useCallback((movedEntry: Entry, snappedPosition: CanvasPosition) => {
		if (movedEntry.type !== EntityType.Reference || !activeGridEntry?.repositoryId || !activeGridAxisSnap?.repositoryId) return;
		const ref = movedEntry as ReferenceEntry;
		if (ref.repositoryId !== activeGridAxisSnap.repositoryId) return;
		const gridDirection = activeGridEntry.direction === 'horizontal' ? 'horizontal' : 'vertical';
		const axisPropertyId = activeGridEntry.primaryPropertyId;
		if (!axisPropertyId) return;
		const rootRefId = findSwimlaneLaneRootIdForCollectionPosition(
			snappedPosition,
			gridDirection,
			projectedSwimlaneVertical,
			projectedSwimlaneHorizontal,
			GRID_SNAP_EPSILON,
		);
		if (!rootRefId) return;
		const rootCard = visibleEntries.find((e) => e.id === rootRefId);
		if (!rootCard || rootCard.type !== EntityType.Reference) return;
		const targetRepositoryEntryId = (rootCard as ReferenceEntry).repositoryEntryId;
		if (!targetRepositoryEntryId) return;
		const def = propertyDefinitionsForGridSnap.find((d) => d.id === axisPropertyId && d.parentId === activeGridEntry.repositoryId);
		if (!def || def.type !== EntityType.PropertyDefinitionBelongsTo) return;
		const collectionEntryId = ref.repositoryEntryId;
		if (!collectionEntryId) return;
		const slug = def.slug;
		const nextValues = [targetRepositoryEntryId];
		if (isAuthenticated) {
			repositoryEntryUpdateMutation({
				id: collectionEntryId,
				values: { [slug]: nextValues },
				repositoryId: activeGridEntry.repositoryId,
			});
		} else {
			dispatch(repositoryEntryUpdateLocalAction({ id: collectionEntryId, values: { [slug]: nextValues } }));
		}
	}, [activeGridAxisSnap, activeGridEntry, dispatch, isAuthenticated, projectedSwimlaneHorizontal, projectedSwimlaneVertical, propertyDefinitionsForGridSnap, repositoryEntryUpdateMutation, visibleEntries]);

	const constrainDragPosition = useCallback((entry: Entry, x: number, y: number): CanvasPosition => {
		const activeGroupDrag = groupDragStateRef.current;
		const isDraggedAsFollowerInActiveGroup = Boolean(
			activeGroupDrag
			&& activeGroupDrag.selectedIds.length > 1
			&& activeGroupDrag.selectedIds.includes(entry.id)
			&& activeGroupDrag.draggedId !== entry.id
		);
		if (isDraggedAsFollowerInActiveGroup) return { x, y };
		const isGridSelectedForSnapDisable = isGridSnapDisabled(
			activeGridEntry,
			selectedEntryIdsRef.current,
			groupDragStateRef.current?.selectedIds
		);
		if (isGridSelectedForSnapDisable) {
			if (dragSnapModeRef.current !== null) {
				dragSnapModeRef.current = null;
				setDragSnapMode(null);
				setActiveSwimlaneCoord(null);
			}
			return { x, y };
		}
		const nextDragSnapMode = resolveReferenceSnapMode(entry, { x, y }, {
			activeGridEntry,
			resolvedAxisSnap: activeGridAxisSnap,
			gridAxisBounds,
			gridSnapX,
			gridSnapY,
			projectedSwimlaneVertical,
			projectedSwimlaneHorizontal,
		}, GRID_X_SNAP_THRESHOLD);
		if (dragSnapModeRef.current !== nextDragSnapMode) {
			dragSnapModeRef.current = nextDragSnapMode;
			setDragSnapMode(nextDragSnapMode);
		}
		const isCollectionEntry = entry.type === EntityType.Reference
			&& Boolean(activeGridAxisSnap?.repositoryId)
			&& (entry as ReferenceEntry).repositoryId === activeGridAxisSnap?.repositoryId;
		if (isCollectionEntry) {
			if (nextDragSnapMode === 'vertical' && projectedSwimlaneVertical.length) {
				const closestLane = projectedSwimlaneVertical.reduce((closest, lane) => (
					Math.abs(x - lane.coord) < Math.abs(x - closest.coord) ? lane : closest
				), projectedSwimlaneVertical[0]);
				setActiveSwimlaneCoord({ orientation: 'vertical', coord: closestLane.coord });
			} else if (nextDragSnapMode === 'horizontal' && projectedSwimlaneHorizontal.length) {
				const closestLane = projectedSwimlaneHorizontal.reduce((closest, lane) => (
					Math.abs(y - lane.coord) < Math.abs(y - closest.coord) ? lane : closest
				), projectedSwimlaneHorizontal[0]);
				setActiveSwimlaneCoord({ orientation: 'horizontal', coord: closestLane.coord });
			} else {
				setActiveSwimlaneCoord(null);
			}
		} else if (activeSwimlaneCoord !== null) {
			setActiveSwimlaneCoord(null);
		}
		return applyReferenceSnap(entry, { x, y }, {
			activeGridEntry,
			resolvedAxisSnap: activeGridAxisSnap,
			gridAxisBounds,
			gridSnapX,
			gridSnapY,
			projectedSwimlaneVertical,
			projectedSwimlaneHorizontal,
		}, GRID_X_SNAP_THRESHOLD);
	}, [activeGridAxisSnap, activeGridEntry, activeSwimlaneCoord, gridAxisBounds, gridSnapX, gridSnapY, groupDragStateRef, projectedSwimlaneHorizontal, projectedSwimlaneVertical, selectedEntryIdsRef, setActiveSwimlaneCoord, setDragSnapMode, dragSnapModeRef]);

	const resolveAnchorId = useCallback((targetEntry: Entry, pos: CanvasPosition): { anchorId?: string | null } => {
		if (targetEntry.type !== EntityType.Reference && targetEntry.type !== EntityType.Note) return {};
		// Landing on an anchor line takes priority — it can re-pin an entry that was previously pinned to a Reference card.
		if (anchorAxisLines?.length) {
			const snappedAnchorId = getSnappedAnchorIdUtil(targetEntry, pos, anchorAxisLines, ANCHOR_SNAP_EPSILON);
			if (snappedAnchorId) return { anchorId: snappedAnchorId };
		}
		const currentAnchorId = (targetEntry as ReferenceEntry | NoteEntry).anchorId ?? null;
		if (currentAnchorId) {
			const currentTarget = visibleEntries.find((e) => e.id === currentAnchorId);
			if (currentTarget?.type === EntityType.Reference) {
				// Pinned directly to a reference card, not a line — this link isn't position-dependent, so moving away (off any line) doesn't clear it.
				return { anchorId: currentAnchorId };
			}
		}
		// Off any line and not reference-pinned — clear a stale line pin (no-op if there wasn't one).
		return anchorAxisLines?.length ? { anchorId: null } : {};
	}, [anchorAxisLines, visibleEntries]);

	const handlePositionChange = useCallback((entry: Entry, x: number, y: number) => {
		if (![EntityType.Note, EntityType.Reference, EntityType.Query, EntityType.Chart, EntityType.Grid, EntityType.Anchor, EntityType.Timeline].includes(entry.type)) return;
		const isGridSelectedForSnapDisable = isGridSnapDisabled(
			activeGridEntry,
			selectedEntryIdsRef.current,
			groupDragStateRef.current?.selectedIds
		);
		const groupDragState = groupDragStateRef.current;
		if (groupDragState && groupDragState.draggedId === entry.id && groupDragState.selectedIds.length > 1) {
			const deltaX = x - groupDragState.draggedStart.x;
			const deltaY = y - groupDragState.draggedStart.y;
			// Track anchor/reference pin-target ids in this group so we can preserve links when moving the target + children together.
			const groupPinTargetIds = new Set(
				groupDragState.selectedIds.filter((id) => {
					const e = visibleEntries.find((e) => e.id === id);
					return e?.type === EntityType.Anchor || e?.type === EntityType.Reference;
				}),
			);
			const payload = groupDragState.selectedIds
				.map((id) => {
					const targetEntry = visibleEntries.find((e) => e.id === id);
					const start = groupDragState.startById.get(id);
					if (!targetEntry || !start) return null;
					const constrained = constrainDragPosition(targetEntry, start.x + deltaX, start.y + deltaY);
					let anchorIdUpdate: { anchorId?: string | null } = {};
					if (targetEntry.type === EntityType.Reference || targetEntry.type === EntityType.Note) {
						const currentAnchorId = (targetEntry as ReferenceEntry | NoteEntry).anchorId ?? null;
						if (currentAnchorId && groupPinTargetIds.has(currentAnchorId)) {
							// Pin target (anchor or reference) is moving with this entry — preserve the link.
							anchorIdUpdate = { anchorId: currentAnchorId };
						} else {
							anchorIdUpdate = resolveAnchorId(targetEntry, constrained);
						}
					}
					return { id: targetEntry.id, type: targetEntry.type, xy: constrained, ...anchorIdUpdate };
				})
				.filter(Boolean) as any[];
			if (payload.length) {
				if (isAuthenticated) entryRepositionMutation(payload);
				else dispatch(entriesUpdateMany(payload));
				for (const row of payload) {
					const targetEntry = visibleEntries.find((e) => e.id === row.id);
					if (targetEntry) syncSwimlaneBelongsToFromPosition(targetEntry, row.xy);
				}
			}
			groupDragStateRef.current = null;
			return;
		}
		if (isGridSelectedForSnapDisable) {
			const payload = [{ id: entry.id, type: entry.type, xy: { x, y }, ...resolveAnchorId(entry, { x, y }) } as any];
			if (isAuthenticated) entryRepositionMutation(payload);
			else dispatch(entriesUpdateMany(payload));
			return;
		}
		const snappedPosition = applyReferenceSnap(entry, { x, y }, {
			activeGridEntry,
			resolvedAxisSnap: activeGridAxisSnap,
			gridAxisBounds,
			gridSnapX,
			gridSnapY,
			projectedSwimlaneVertical,
			projectedSwimlaneHorizontal,
		}, GRID_X_SNAP_THRESHOLD);
		const payload = [{ id: entry.id, type: entry.type, xy: snappedPosition, ...resolveAnchorId(entry, snappedPosition) } as any];
		if (isAuthenticated) entryRepositionMutation(payload);
		else dispatch(entriesUpdateMany(payload));
		syncSwimlaneBelongsToFromPosition(entry, snappedPosition);
	}, [activeGridAxisSnap, activeGridEntry, constrainDragPosition, dispatch, entryRepositionMutation, gridAxisBounds, gridSnapX, gridSnapY, groupDragStateRef, isAuthenticated, projectedSwimlaneHorizontal, projectedSwimlaneVertical, resolveAnchorId, selectedEntryIdsRef, syncSwimlaneBelongsToFromPosition, visibleEntries]);

	const getConstrainedDragPosition = useCallback(
		(entry: Entry, x: number, y: number): CanvasPosition => constrainDragPosition(entry, x, y),
		[constrainDragPosition]
	);

	return {
		activeGridEntry,
		activeGridAxisSnap,
		gridSnapX,
		gridSnapY,
		gridAxisBounds,
		projectedSwimlaneVertical,
		projectedSwimlaneHorizontal,
		isDraggingYAxisReference,
		isDraggingXAxisReference,
		isDraggingCollectionReference,
		swimlaneChildIdsByRootId,
		getIdsLinkedToGridAtAnchor,
		expandSelectedIdsWithSwimlaneChildren,
		applyGridAxisPreviewDelta,
		applySwimlanePreviewDeltaForRoot,
		getConstrainedDragPosition,
		handlePositionChange,
	};
}
